import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {
  IconLock,
  IconUserCheck,
  IconUserOff,
  IconUserShield,
} from '@tabler/icons-react';
import type {FC} from 'react';
import {UserStatusEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {IconSize} from 'src/design/atoms/icons/CommonIcons';
import type {PillProps} from 'src/design/atoms/Pill';
import {Pill} from 'src/design/atoms/Pill';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {SupportDetails} from './SupportDetails';

const ACCESS_VIEW: Record<UserStatusEnum, {pill: PillProps; summary: string}> =
  {
    [UserStatusEnum.Active]: {
      pill: {text: 'Active', color: 'green'},
      summary:
        'Can access this company according to their assigned permissions.',
    },
    [UserStatusEnum.Pending]: {
      pill: {text: 'Pending', color: 'yellow'},
      summary:
        'Has training-only access to this company. Used while onboarding.',
    },
    [UserStatusEnum.Inactive]: {
      pill: {text: 'Inactive', color: 'gray'},
      summary:
        'Cannot access this company. They may still sign in to other companies where they have access.',
    },
  };

export interface AccessCardProps {
  active: UserStatusEnum;
  onActivate: () => void;
  onLock: () => void;
  onDeactivate: () => void;
  activating: boolean;
  locking: boolean;
  deactivating: boolean;
  disabled: boolean;
}

export const AccessCard: FC<AccessCardProps> = ({
  active,
  onActivate,
  onLock,
  onDeactivate,
  activating,
  locking,
  deactivating,
  disabled,
}) => {
  const view = ACCESS_VIEW[active];
  return (
    <SectionCard
      title="Access"
      icon={<IconUserShield size={IconSize.md} />}
      rightSection={<Pill {...view.pill} />}>
      <Stack p="md" gap="md">
        <Text size="sm">{view.summary}</Text>
        <Text size="sm" c="dimmed">
          Activate restores access to this company. Lock allows training-only
          access. Deactivate removes access to this company and can interrupt
          work in progress.
        </Text>
        <Group gap="xs">
          {active !== UserStatusEnum.Active && (
            <Button
              color="green"
              onClick={onActivate}
              loading={activating}
              disabled={disabled}
              leftSection={<IconUserCheck size={IconSize.md} />}>
              Activate
            </Button>
          )}
          {active !== UserStatusEnum.Pending && (
            <Button
              variant="outline"
              color="yellow"
              onClick={onLock}
              loading={locking}
              disabled={disabled}
              leftSection={<IconLock size={IconSize.md} />}>
              Lock
            </Button>
          )}
          {active !== UserStatusEnum.Inactive && (
            <Button
              variant="outline"
              color="red"
              onClick={onDeactivate}
              loading={deactivating}
              disabled={disabled}
              leftSection={<IconUserOff size={IconSize.md} />}>
              Deactivate
            </Button>
          )}
        </Group>
        <SupportDetails title="Help with company access">
          <Text size="sm">
            These controls apply to this company. They do not change access to
            other companies, reset the shared login, or change the user's
            assigned permissions.
          </Text>
          <Text size="sm">
            Deactivation keeps the profile and history, but cancels open or
            pending toolbox talks and removes company group memberships.
            Reactivating the user does not automatically restore these. Let the
            user know before changing access during training or other work.
          </Text>
          <Text size="sm">
            If the user cannot access this company after signing in, check their
            status here first. If access is active, check their permissions or
            contact Proact support.
          </Text>
        </SupportDetails>
      </Stack>
    </SectionCard>
  );
};
